import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Mic, Plus, Trash2, Edit, FolderPlus, ChevronRight, ChevronDown,
  Play, Pause, Clock, Eye, Music, BarChart2, Folder, X, Save,
  Upload, Link, GripVertical, MoreVertical, Headphones
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PodcastManagement = () => {
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showPodcastModal, setShowPodcastModal] = useState(false);
  
  // Edit states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editingPodcast, setEditingPodcast] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', icon: '🎙️', color: '#6366f1' });
  const [subcategoryForm, setSubcategoryForm] = useState({ name: '', description: '', icon: '📁', category_id: '' });
  const [podcastForm, setPodcastForm] = useState({ 
    title: '', description: '', audio_url: '', duration_text: '', 
    thumbnail: '', transcript: '', subcategory_id: '', is_featured: false 
  });
  
  // Selected parent for adding
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  
  // Podcasts for selected subcategory
  const [podcasts, setPodcasts] = useState([]);
  const [loadingPodcasts, setLoadingPodcasts] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [catRes, statsRes] = await Promise.all([
        axios.get(`${API}/podcasts/categories`, { withCredentials: true }),
        axios.get(`${API}/admin/podcasts/stats`, { withCredentials: true })
      ]);
      setCategories(catRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadPodcasts = async (subcategoryId) => {
    setLoadingPodcasts(true);
    try {
      const res = await axios.get(`${API}/podcasts/list?subcategory_id=${subcategoryId}`, { withCredentials: true });
      setPodcasts(res.data);
    } catch (err) {
      console.error('Podcast load error:', err);
    } finally {
      setLoadingPodcasts(false);
    }
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const toggleSubcategory = async (subcatId) => {
    const newState = !expandedSubcategories[subcatId];
    setExpandedSubcategories(prev => ({ ...prev, [subcatId]: newState }));
    if (newState) {
      setSelectedSubcategoryId(subcatId);
      await loadPodcasts(subcatId);
    }
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/podcasts/categories/${editingCategory.id}`, categoryForm, { withCredentials: true });
        toast.success('Kategori güncellendi');
      } else {
        await axios.post(`${API}/admin/podcasts/categories`, categoryForm, { withCredentials: true });
        toast.success('Kategori oluşturuldu');
      }
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '', icon: '🎙️', color: '#6366f1' });
      setEditingCategory(null);
      loadData();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/podcasts/categories/${catId}`, { withCredentials: true });
      toast.success('Kategori silindi');
      loadData();
    } catch (err) {
      toast.error('Silme başarısız');
    }
  };

  // Subcategory CRUD
  const handleSaveSubcategory = async () => {
    try {
      const data = { ...subcategoryForm, category_id: selectedCategoryId };
      if (editingSubcategory) {
        await axios.put(`${API}/admin/podcasts/subcategories/${editingSubcategory.id}`, data, { withCredentials: true });
        toast.success('Alt kategori güncellendi');
      } else {
        await axios.post(`${API}/admin/podcasts/subcategories`, data, { withCredentials: true });
        toast.success('Alt kategori oluşturuldu');
      }
      setShowSubcategoryModal(false);
      setSubcategoryForm({ name: '', description: '', icon: '📁', category_id: '' });
      setEditingSubcategory(null);
      loadData();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteSubcategory = async (subcatId) => {
    if (!window.confirm('Bu alt kategoriyi silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/podcasts/subcategories/${subcatId}`, { withCredentials: true });
      toast.success('Alt kategori silindi');
      loadData();
    } catch (err) {
      toast.error('Silme başarısız');
    }
  };

  // Podcast CRUD
  const handleSavePodcast = async () => {
    try {
      const data = { ...podcastForm, subcategory_id: selectedSubcategoryId };
      if (editingPodcast) {
        await axios.put(`${API}/admin/podcasts/${editingPodcast.id}`, data, { withCredentials: true });
        toast.success('Podcast güncellendi');
      } else {
        await axios.post(`${API}/admin/podcasts`, data, { withCredentials: true });
        toast.success('Podcast oluşturuldu');
      }
      setShowPodcastModal(false);
      setPodcastForm({ title: '', description: '', audio_url: '', duration_text: '', thumbnail: '', transcript: '', subcategory_id: '', is_featured: false });
      setEditingPodcast(null);
      loadPodcasts(selectedSubcategoryId);
      loadData();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeletePodcast = async (podcastId) => {
    if (!window.confirm('Bu podcast\'i silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/podcasts/${podcastId}`, { withCredentials: true });
      toast.success('Podcast silindi');
      loadPodcasts(selectedSubcategoryId);
      loadData();
    } catch (err) {
      toast.error('Silme başarısız');
    }
  };

  const iconOptions = ['🎙️', '🎧', '📻', '🎤', '💼', '✈️', '🌍', '📚', '💡', '🎯', '⭐', '🔥', '📁', '📂', '🗂️'];
  const colorOptions = ['#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#0ea5e9', '#3b82f6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Mic className="w-7 h-7 text-purple-500" />
            Podcast Yönetimi
          </h2>
          <p className="text-gray-500 mt-1">Kategoriler, alt başlıklar ve podcast'leri yönetin</p>
        </div>
        <Button 
          onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', description: '', icon: '🎙️', color: '#6366f1' }); setShowCategoryModal(true); }}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kategori
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Kategoriler</p>
                  <p className="text-2xl font-bold">{stats.total_categories}</p>
                </div>
                <Folder className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Alt Kategoriler</p>
                  <p className="text-2xl font-bold">{stats.total_subcategories}</p>
                </div>
                <FolderPlus className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Podcast'ler</p>
                  <p className="text-2xl font-bold">{stats.total_podcasts}</p>
                </div>
                <Headphones className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Toplam Dinlenme</p>
                  <p className="text-2xl font-bold">{stats.total_views}</p>
                </div>
                <BarChart2 className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Categories Tree */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="w-5 h-5 text-purple-500" />
            Podcast Yapısı
          </CardTitle>
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Mic className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Henüz kategori yok</p>
              <p className="text-sm">İlk kategoriyi oluşturmak için "Yeni Kategori" butonuna tıklayın</p>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((category) => (
                <div key={category.id} className="border rounded-xl overflow-hidden">
                  {/* Category Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderLeft: `4px solid ${category.color}` }}
                  >
                    <div className="flex items-center gap-3 flex-1" onClick={() => toggleCategory(category.id)}>
                      {expandedCategories[category.id] ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="text-2xl">{category.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-800">{category.name}</h3>
                        <p className="text-sm text-gray-500">{category.subcategories?.length || 0} alt kategori</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setSelectedCategoryId(category.id); setEditingSubcategory(null); setSubcategoryForm({ name: '', description: '', icon: '📁' }); setShowSubcategoryModal(true); }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`add-subcategory-${category.id}`}
                      >
                        <FolderPlus className="w-4 h-4 mr-1" />
                        Alt Kategori
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setEditingCategory(category); setCategoryForm({ name: category.name, description: category.description, icon: category.icon, color: category.color }); setShowCategoryModal(true); }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Subcategories */}
                  {expandedCategories[category.id] && category.subcategories?.length > 0 && (
                    <div className="bg-gray-50 border-t">
                      {category.subcategories.map((subcat) => (
                        <div key={subcat.id}>
                          {/* Subcategory Header */}
                          <div className="flex items-center justify-between px-6 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200">
                            <div className="flex items-center gap-3 flex-1" onClick={() => toggleSubcategory(subcat.id)}>
                              {expandedSubcategories[subcat.id] ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="text-lg">{subcat.icon}</span>
                              <div>
                                <h4 className="font-medium text-gray-700">{subcat.name}</h4>
                                <p className="text-xs text-gray-500">{subcat.podcast_count || 0} podcast</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setSelectedSubcategoryId(subcat.id); setEditingPodcast(null); setPodcastForm({ title: '', description: '', audio_url: '', duration_text: '', thumbnail: '', transcript: '', subcategory_id: '', is_featured: false }); setShowPodcastModal(true); }}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`add-podcast-${subcat.id}`}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Podcast
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setSelectedCategoryId(category.id); setEditingSubcategory(subcat); setSubcategoryForm({ name: subcat.name, description: subcat.description, icon: subcat.icon }); setShowSubcategoryModal(true); }}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleDeleteSubcategory(subcat.id); }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Podcasts List */}
                          {expandedSubcategories[subcat.id] && (
                            <div className="bg-white px-8 py-2">
                              {loadingPodcasts && selectedSubcategoryId === subcat.id ? (
                                <div className="py-4 text-center text-gray-500">Yükleniyor...</div>
                              ) : podcasts.length > 0 && selectedSubcategoryId === subcat.id ? (
                                <div className="space-y-2 py-2">
                                  {podcasts.map((podcast) => (
                                    <div 
                                      key={podcast.id} 
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                          <Headphones className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                          <h5 className="font-medium text-gray-800">{podcast.title}</h5>
                                          <div className="flex items-center gap-3 text-xs text-gray-500">
                                            <span className="flex items-center gap-1">
                                              <Clock className="w-3 h-3" />
                                              {podcast.duration_text || '0:00'}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <Eye className="w-3 h-3" />
                                              {podcast.view_count || 0}
                                            </span>
                                            {podcast.is_featured && (
                                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">Öne Çıkan</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {podcast.audio_url && (
                                          <a 
                                            href={podcast.audio_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                          >
                                            <Play className="w-4 h-4" />
                                          </a>
                                        )}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => { setSelectedSubcategoryId(subcat.id); setEditingPodcast(podcast); setPodcastForm({ title: podcast.title, description: podcast.description, audio_url: podcast.audio_url, duration_text: podcast.duration_text, thumbnail: podcast.thumbnail, transcript: podcast.transcript, subcategory_id: podcast.subcategory_id, is_featured: podcast.is_featured }); setShowPodcastModal(true); }}
                                          className="text-gray-500 hover:text-gray-700"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDeletePodcast(podcast.id)}
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : selectedSubcategoryId === subcat.id && (
                                <div className="py-4 text-center text-gray-500 text-sm">
                                  Bu alt kategoride henüz podcast yok
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty subcategories message */}
                  {expandedCategories[category.id] && (!category.subcategories || category.subcategories.length === 0) && (
                    <div className="bg-gray-50 border-t px-6 py-4 text-center text-gray-500 text-sm">
                      Bu kategoride henüz alt kategori yok
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between text-white">
                <h3 className="font-bold text-lg">
                  {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
                </h3>
                <button onClick={() => setShowCategoryModal(false)} className="p-1 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Kategori Adı *</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Örn: Mülakat İpuçları"
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label>Açıklama</Label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Kategori açıklaması"
                  rows={2}
                />
              </div>
              <div>
                <Label>İkon</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => setCategoryForm({ ...categoryForm, icon })}
                      className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                        categoryForm.icon === icon ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Renk</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      onClick={() => setCategoryForm({ ...categoryForm, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        categoryForm.color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowCategoryModal(false)}>
                  İptal
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={handleSaveCategory}
                  disabled={!categoryForm.name}
                  data-testid="save-category-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between text-white">
                <h3 className="font-bold text-lg">
                  {editingSubcategory ? 'Alt Kategori Düzenle' : 'Yeni Alt Kategori'}
                </h3>
                <button onClick={() => setShowSubcategoryModal(false)} className="p-1 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Alt Kategori Adı *</Label>
                <Input
                  value={subcategoryForm.name}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                  placeholder="Örn: Grup Mülakatı Taktikleri"
                  data-testid="subcategory-name-input"
                />
              </div>
              <div>
                <Label>Açıklama</Label>
                <Textarea
                  value={subcategoryForm.description}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
                  placeholder="Alt kategori açıklaması"
                  rows={2}
                />
              </div>
              <div>
                <Label>İkon</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => setSubcategoryForm({ ...subcategoryForm, icon })}
                      className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                        subcategoryForm.icon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowSubcategoryModal(false)}>
                  İptal
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleSaveSubcategory}
                  disabled={!subcategoryForm.name}
                  data-testid="save-subcategory-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Podcast Modal */}
      {showPodcastModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-8">
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between text-white">
                <h3 className="font-bold text-lg">
                  {editingPodcast ? 'Podcast Düzenle' : 'Yeni Podcast'}
                </h3>
                <button onClick={() => setShowPodcastModal(false)} className="p-1 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <Label>Podcast Başlığı *</Label>
                <Input
                  value={podcastForm.title}
                  onChange={(e) => setPodcastForm({ ...podcastForm, title: e.target.value })}
                  placeholder="Örn: THY Mülakatında En Çok Sorulan Sorular"
                  data-testid="podcast-title-input"
                />
              </div>
              <div>
                <Label>Açıklama</Label>
                <Textarea
                  value={podcastForm.description}
                  onChange={(e) => setPodcastForm({ ...podcastForm, description: e.target.value })}
                  placeholder="Podcast içeriği hakkında kısa bilgi"
                  rows={2}
                />
              </div>
              <div>
                <Label>Ses Dosyası *</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Input
                      value={podcastForm.audio_url}
                      onChange={(e) => setPodcastForm({ ...podcastForm, audio_url: e.target.value })}
                      placeholder="URL girin veya dosya yükleyin"
                      data-testid="podcast-audio-input"
                    />
                    <Button variant="outline" size="icon" title="URL yapıştır">
                      <Link className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="audio/*"
                      id="audio-upload"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          toast.loading('Ses dosyası yükleniyor...');
                          const res = await axios.post(`${API}/admin/podcasts/upload-audio`, formData, { 
                            withCredentials: true,
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          toast.dismiss();
                          toast.success('Ses dosyası yüklendi!');
                          setPodcastForm({ ...podcastForm, audio_url: res.data.url });
                        } catch (err) {
                          toast.dismiss();
                          toast.error('Yükleme hatası: ' + (err.response?.data?.detail || err.message));
                        }
                      }}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => document.getElementById('audio-upload').click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Bilgisayardan Yükle
                    </Button>
                  </div>
                  {podcastForm.audio_url && (
                    <audio controls className="w-full mt-2" src={podcastForm.audio_url.startsWith('http') ? podcastForm.audio_url : BACKEND_URL + podcastForm.audio_url} />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Süre</Label>
                  <Input
                    value={podcastForm.duration_text}
                    onChange={(e) => setPodcastForm({ ...podcastForm, duration_text: e.target.value })}
                    placeholder="Örn: 5:30"
                  />
                </div>
                <div>
                  <Label>Thumbnail URL</Label>
                  <Input
                    value={podcastForm.thumbnail}
                    onChange={(e) => setPodcastForm({ ...podcastForm, thumbnail: e.target.value })}
                    placeholder="Kapak resmi URL"
                  />
                </div>
              </div>
              <div>
                <Label>Transkript (Opsiyonel)</Label>
                <Textarea
                  value={podcastForm.transcript}
                  onChange={(e) => setPodcastForm({ ...podcastForm, transcript: e.target.value })}
                  placeholder="Podcast metni..."
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={podcastForm.is_featured}
                  onChange={(e) => setPodcastForm({ ...podcastForm, is_featured: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_featured" className="cursor-pointer">Öne çıkan podcast olarak işaretle</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowPodcastModal(false)}>
                  İptal
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleSavePodcast}
                  disabled={!podcastForm.title || !podcastForm.audio_url}
                  data-testid="save-podcast-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastManagement;
